<script setup lang="ts">
import { computed } from 'vue'
import CharacterCard from '@/components/characters/CharacterCard.vue'
import type { Character } from '@/types/domain'

type DiscoveryCharacter = Character & {
  tagline?: string | null
}

const props = withDefaults(defineProps<{
  characters?: DiscoveryCharacter[]
  loading?: boolean
}>(), {
  characters: () => [],
  loading: false,
})

const emit = defineEmits<{
  (e: 'open-character', character: DiscoveryCharacter): void
  (e: 'view-all'): void
}>()

const displayCharacters = computed(() => props.characters.slice(0, 6))
</script>

<template>
  <section class="discovery-section">
    <div class="discovery-section__heading-row">
      <h2 class="discovery-section__heading">Start something new</h2>
      <button
        v-if="props.characters.length > 6"
        type="button"
        class="discovery-section__view-all"
        @click="emit('view-all')"
      >
        View all +
      </button>
    </div>

    <div v-if="props.loading" class="discovery-section__grid">
      <div
        v-for="i in 4"
        :key="i"
        class="discovery-card discovery-card--skeleton"
        aria-hidden="true"
      >
        <div class="discovery-card__portrait--skeleton" />
        <div class="discovery-card__body">
          <div class="discovery-card__skeleton-line discovery-card__skeleton-line--title" />
          <div class="discovery-card__skeleton-line" />
        </div>
      </div>
    </div>

    <div v-else-if="displayCharacters.length === 0" class="discovery-section__empty">
      <p class="discovery-section__empty-text">
        <button
          type="button"
          class="discovery-section__browse-link"
          @click="emit('view-all')"
        >
          Browse all characters
        </button>
      </p>
    </div>

    <div v-else class="discovery-section__grid">
      <CharacterCard
        v-for="character in displayCharacters"
        :key="character.id"
        class="discovery-card"
        :title="character.character_name"
        :tagline="character.tagline || ''"
        :image-src="character.imgpath || undefined"
        :image-alt="character.character_name"
        :image-focus="character.img_focus"
        :tags="character.tags || []"
        :description="character.description || ''"
        :favorited="Boolean(character.is_favorited)"
        :number-favorites="character.favorites_count"
        :message-count-total="character.message_count_total"
        :avg-messages-per-conversation="character.avg_messages_per_conversation"
        :tagline-line-clamp="6"
        @click="emit('open-character', character)"
      />
    </div>
  </section>
</template>

<style scoped>
.discovery-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.discovery-section__heading-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.discovery-section__heading {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--theme-text-secondary);
  margin: 0;
}

.discovery-section__view-all {
  background: transparent;
  border: none;
  color: var(--theme-primary);
  font-size: 0.83rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  transition: opacity 150ms;
}

.discovery-section__view-all:hover {
  opacity: 0.8;
}

.discovery-section__empty {
  padding: 24px 0;
}

.discovery-section__empty-text {
  font-size: 0.9rem;
  color: var(--theme-text-tertiary);
  margin: 0;
}

.discovery-section__browse-link {
  background: transparent;
  border: none;
  color: var(--theme-primary);
  font-size: inherit;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.discovery-section__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

@media (min-width: 640px) {
  .discovery-section__grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 900px) {
  .discovery-section__grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
}

.discovery-card {
  width: 100%;
}

.discovery-card--skeleton {
  pointer-events: none;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--theme-surface);
  border: 1px solid var(--theme-border);
  border-radius: 12px;
  overflow: hidden;
}

.discovery-card__portrait--skeleton {
  width: 100%;
  aspect-ratio: 3 / 4;
  background: var(--theme-shimmer-from);
  animation: discovery-shimmer 1.4s ease-in-out infinite;
}

.discovery-card__body {
  padding: 12px 13px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.discovery-card__skeleton-line {
  height: 14px;
  border-radius: 7px;
  background: var(--theme-shimmer-to);
  animation: discovery-shimmer 1.4s ease-in-out infinite;
  width: 100%;
}

.discovery-card__skeleton-line--title {
  height: 16px;
  width: 70%;
}

@keyframes discovery-shimmer {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}
</style>
